/* HenaketStepper - the Henaket "Step" navigator.
 *
 * 0.42.66 - A chip reading "Step n of m" that opens a panel listing
 * every step, each row carrying a number, a title, an optional
 * description, a completion check and the current marker.
 *
 * Markup follows the spec's own recommendation for its open question on
 * semantics: a disclosure button plus a <nav> and an <ol>, with
 * aria-current="step" on the current row. That is what a step navigator
 * IS - navigation with one place marked - where a listbox would promise
 * selection and a progress bar would promise a value.
 */

import { useEffect, useRef, useState } from 'react';

import styles from './HenaketStepper.module.css';

const CARET = 'M0 0h10L5 5z';
// Material "check", the 18x13 ink in a 24x24 box the spec measures.
const CHECK = 'M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z';

export default function HenaketStepper({
  steps = [],
  currentIndex = 0,
  onGoTo = null,
  label = 'Step {n} of {m}',
  /* Authoring surfaces draw the panel open, and IN the flow rather than
     over the page, so an admin can see what they are placing without it
     covering whatever comes next. */
  alwaysOpen = false,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const shown = alwaysOpen || open;

  /* A click outside or Escape closes it. Escape returns focus to the
     chip, or a keyboard user is left nowhere. */
  useEffect(() => {
    if (!open || alwaysOpen) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      const btn = rootRef.current && rootRef.current.querySelector('button');
      if (btn) btn.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, alwaysOpen]);

  const total = steps.length;
  if (total === 0) return null;
  const n = Math.min(total, Math.max(1, currentIndex + 1));
  const chip = String(label || '')
    .replace('{n}', String(n))
    .replace('{m}', String(total));

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={styles.trigger}
        aria-expanded={shown ? 'true' : 'false'}
        onClick={() => setOpen(v => !v)}
      >
        {chip}
        <svg className={styles.caret} viewBox="0 0 10 5" aria-hidden="true">
          <path d={CARET} />
        </svg>
      </button>

      {shown && (
        <nav className={`${styles.panel} ${alwaysOpen ? styles.panelInFlow : ''}`}
             aria-label={chip}>
          <ol className={styles.list}>
            {steps.map((s, i) => {
              const isCurrent = i === currentIndex;
              // A step is complete once the citizen has been past it.
              const complete = !!s.complete;
              // Nothing ahead of where they have reached is reachable:
              // a later step may depend on answers not given yet.
              const locked = !!s.locked;
              const rowClass = [
                styles.row,
                isCurrent ? styles.current : '',
                locked ? styles.locked : '',
              ].filter(Boolean).join(' ');
              return (
                <li key={s.key || i} className={rowClass}
                    aria-current={isCurrent ? 'step' : undefined}>
                  <button
                    type="button"
                    className={styles.item}
                    disabled={locked || !onGoTo}
                    onClick={() => { if (onGoTo) { onGoTo(i); setOpen(false); } }}
                  >
                    <span className={styles.text}>
                      {/* 0.45.74 - The text wraps, so the whole of it is
                          shown and no tooltip is needed. */}
                      <span className={styles.title}>
                        {`${i + 1}. ${s.title || ''}`}
                      </span>
                      {s.description ? (
                        <span className={styles.desc}>
                          {s.description}
                        </span>
                      ) : null}
                    </span>
                    {complete && (
                      <>
                        <svg className={styles.check} viewBox="0 0 24 24" aria-hidden="true">
                          <path d={CHECK} />
                        </svg>
                        <span className="sr-only">Completed</span>
                      </>
                    )}
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>
      )}
    </div>
  );
}
